import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnDestroy,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import { Subscription } from 'rxjs';

import { AskDraftHandoffService } from '@core/services/ask-draft-handoff.service';
import { CategoryService } from '@core/services/category.service';
import { AuthService } from '../../services/AuthService';
import { CategoriesStore } from '@state/categories.store';
import { getAvatarInitials } from '@shared/utils/avatar-initials.util';
import {
  AgentAvailabilityResponse,
  AgentChatRequest,
  AgentChatStreamEvent,
  AgentFeedbackRequest,
  HealthNavService,
  isAiRateLimitError
} from './health-nav.service';
import {
  ChatMessage,
  ChatAction,
  ChatSuggestion,
  ChatStructuredContent,
  CommunityJoinVm,
  DEFAULT_CHIPS,
  FeedbackSentiment,
  GuidedEntryMode,
  PostResultVm,
  RewriteDraftVm
} from './health-nav.models';
import { HealthNavChatComponent } from './health-nav-chat.component';

type CommunityLookup = {
  id: string | number;
  name: string;
  members?: number;
  memberCount?: number;
  description?: string;
  tagline?: string;
  slug?: string;
};

@Component({
  selector: 'app-health-nav-widget',
  standalone: true,
  imports: [CommonModule, HealthNavChatComponent],
  templateUrl: './health-nav-widget.component.html',
  styleUrls: ['./health-nav-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideUp', [
      state('void', style({ opacity: 0, transform: 'translateY(20px) scale(0.95)' })),
      state('*', style({ opacity: 1, transform: 'translateY(0) scale(1)' })),
      transition(':enter', animate('200ms cubic-bezier(0.34, 1.56, 0.64, 1)')),
      transition(':leave', animate('150ms ease-in'))
    ])
  ]
})
export class HealthNavWidgetComponent implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly healthNavService = inject(HealthNavService);
  private readonly categoriesStore = inject(CategoriesStore);
  private readonly askDraftHandoff = inject(AskDraftHandoffService);
  private readonly categoryService = inject(CategoryService);
  private readonly router = inject(Router);
  private readonly submittedFeedbackTraceIds = new Set<string>();

  private availabilitySubscription?: Subscription;
  private navigationSubscription?: Subscription;
  private streamSubscription?: Subscription;
  private inactivityTimer?: ReturnType<typeof setTimeout>;
  private lastAssistantTraceId: string | null = null;
  private recentContext: unknown;
  private pendingRequestContext: { text: string; guidedMode: GuidedEntryMode | null } | null = null;

  @Input() withBottomNav = false;
  @Input() avoidMobileActionFab = false;

  @HostBinding('class.health-nav-widget-host--with-bottom-nav')
  get hasBottomNavOffset(): boolean {
    return this.withBottomNav;
  }

  @HostBinding('class.health-nav-widget-host--avoid-mobile-action')
  get avoidsMobileActionFab(): boolean {
    return this.avoidMobileActionFab;
  }

  readonly isOpen = signal(false);
  readonly hasUnread = signal(false);
  readonly isStreaming = signal(false);
  readonly inputText = signal('');
  readonly errorMessage = signal('');
  readonly widgetVisible = signal(true);
  readonly widgetAvailable = signal(true);
  readonly conversationId = signal(this.createConversationId());
  readonly messages = signal<ChatMessage[]>([this.createWelcomeMessage()]);
  readonly guidedMode = signal<GuidedEntryMode | null>(null);
  readonly loadingLabel = signal('Thinking...');
  readonly focusRequest = signal(0);

  readonly showWidget = computed(() =>
    this.authService.authReady() &&
    this.authService.isAuthenticated() &&
    this.widgetVisible()
  );

  readonly assistantAvailable = computed(() => this.showWidget() && this.widgetAvailable());
  readonly suggestionChips = computed<ChatSuggestion[]>(() => this.buildSuggestionChips());
  readonly contextualActionMessage = computed(() => this.getLatestAssistantMessage());
  readonly contextualActions = computed(() =>
    this.guidedMode() ? [] : this.contextualActionsFor(this.contextualActionMessage())
  );
  readonly contextualActionMessageId = computed(() => this.contextualActionMessage()?.id ?? null);
  readonly currentGuidedMode = computed(() => this.guidedMode());
  readonly inputPlaceholder = computed(() => this.buildInputPlaceholder(this.guidedMode()));
  readonly inputHelperText = computed(() => this.buildInputHelperText(this.guidedMode()));
  readonly inlineNotice = computed(() =>
    !this.widgetAvailable()
      ? this.buildAssistantUnavailableMessage()
      : this.errorMessage()
  );
  readonly inlineNoticeTone = computed<'info' | 'warning' | 'error'>(() => {
    if (!this.widgetAvailable()) {
      return 'info';
    }

    return this.isRateLimitMessage(this.errorMessage()) ? 'warning' : 'error';
  });

  constructor() {
    this.categoriesStore.loadFromApi();

    this.navigationSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart && this.isMobileViewport() && this.isOpen()) {
        this.closePanel(false);
      }
    });

    if (typeof document !== 'undefined') {
      document.addEventListener('click', this.handleMobileMenuClick, true);
    }

    effect(() => {
      const ready = this.authService.authReady();
      const authenticated = this.authService.isAuthenticated();

      if (ready && authenticated) {
        this.startAvailabilityWatch();
        return;
      }

      this.stopAvailabilityWatch();
      this.closePanel(true);
      this.widgetVisible.set(false);
      this.widgetAvailable.set(false);
    });
  }

  ngOnDestroy(): void {
    this.stopAvailabilityWatch();
    this.stopStreaming();
    this.clearInactivityTimer();
    this.navigationSubscription?.unsubscribe();

    if (typeof document !== 'undefined') {
      document.removeEventListener('click', this.handleMobileMenuClick, true);
    }
  }

  private readonly handleMobileMenuClick = (event: MouseEvent): void => {
    if (!this.isOpen() || !this.isMobileViewport()) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('app-bottom-nav a, app-bottom-nav button, [role="menuitem"]')) {
      this.closePanel(false);
    }
  };

  private isMobileViewport(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 479px)').matches;
  }

  togglePanel(): void {
    if (!this.assistantAvailable()) return;
    this.isOpen() ? this.closePanel(false) : this.openPanel();
  }

  openPanel(): void {
    if (!this.assistantAvailable()) return;
    this.isOpen.set(true);
    this.hasUnread.set(false);
    this.focusRequest.update(value => value + 1);
    this.touchConversation();
  }

  closePanel(resetSession: boolean): void {
    this.isOpen.set(false);
    this.hasUnread.set(false);
    this.errorMessage.set('');
    this.guidedMode.set(null);
    this.stopStreaming();
    this.clearInactivityTimer();

    if (resetSession) {
      this.resetConversationState();
    }
  }

  handleTextChange(value: string): void {
    this.inputText.set(value);
    this.touchConversation();
  }

  sendMessage(messageText: string): void {
    const text = messageText.trim();
    if (!text || this.isStreaming() || !this.assistantAvailable()) return;

    const guidedMode = this.guidedMode();
    const outgoingText = guidedMode
      ? this.buildGuidedMessageText(guidedMode, text)
      : text;
    this.loadingLabel.set(this.buildLoadingLabel(guidedMode, outgoingText));
    this.pendingRequestContext = { text, guidedMode };
    this.guidedMode.set(null);

    const assistantMessageId = this.createMessageId();
    const userMessage: ChatMessage = {
      id: this.createMessageId(),
      role: 'user',
      text,
      isStreaming: false,
      crisis: false,
      blocked: false,
      followUp: false,
      responseComplete: true,
      timestamp: new Date()
    };

    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      text: '',
      isStreaming: true,
      crisis: false,
      blocked: false,
      followUp: false,
      responseComplete: false,
      timestamp: new Date()
    };

    const safetyMessage = this.buildSafetyGuardrailMessage(text);
    this.messages.update(existing => [
      ...existing,
      ...(safetyMessage ? [safetyMessage] : []),
      userMessage,
      assistantMessage
    ]);
    this.inputText.set('');
    this.errorMessage.set('');
    this.isStreaming.set(true);
    this.isOpen.set(true);
    this.hasUnread.set(false);
    this.touchConversation();

    const request: AgentChatRequest = {
      message: outgoingText,
      mode: this.modeForGuidedMode(guidedMode),
      communityId: this.resolveCommunityIdFromRoute(),
      conversationId: this.conversationId(),
      recentContext: this.recentContext
    };

    this.stopStreaming();
    this.streamSubscription = this.healthNavService.chatStream(request).subscribe({
      next: event => this.handleStreamEvent(assistantMessageId, event),
      error: error => {
        const fallback = this.normalizeChatError(error);

        this.updateMessage(assistantMessageId, {
          text: fallback,
          isStreaming: false,
          responseComplete: true
        });
        this.isStreaming.set(false);
        this.loadingLabel.set('Thinking...');
        this.errorMessage.set(fallback);
        this.restoreComposerState();
        this.touchConversation();
      },
      complete: () => {
        this.finishAssistantMessage(assistantMessageId);
      }
    });
  }

  sendChip(chip: ChatSuggestion | string): void {
    const prompt = typeof chip === 'string' ? chip : chip.prompt;
    const guidedMode = typeof chip === 'string'
      ? this.resolveGuidedMode(prompt)
      : chip.guidedMode ?? this.resolveGuidedMode(chip.label);

    if (guidedMode) {
      this.enterGuidedMode(guidedMode);
      return;
    }

    this.inputText.set(prompt);
    this.sendMessage(prompt);
  }

  sendFeedback(messageId: string, sentiment: FeedbackSentiment): void {
    const message = this.messages().find(entry => entry.id === messageId);
    if (!message?.traceId || message.feedbackPending || message.feedbackSubmitted) return;

    this.updateMessage(messageId, {
      feedbackPending: true,
      feedbackSent: sentiment,
      feedbackError: ''
    });

    const request: AgentFeedbackRequest = {
      traceId: message.traceId,
      sentiment,
      reasonCode: undefined
    };

    this.healthNavService.submitFeedback(request).subscribe({
      next: () => {
        this.submittedFeedbackTraceIds.add(message.traceId!);
        this.updateMessage(messageId, {
          feedbackPending: false,
          feedbackSubmitted: true
        });
      },
      error: () => {
        this.updateMessage(messageId, {
          feedbackPending: false,
          feedbackError: 'Could not save feedback.'
        });
      }
    });
  }

  useDraft(messageId: string): void {
    const message = this.messages().find(entry => entry.id === messageId);
    const draft = message?.structured?.kind === 'rewrite_post' ? message.structured.draft : null;
    if (!draft) return;

    this.askDraftHandoff.setDraft({
      title: draft.title,
      body: draft.body,
      source: 'ai-rewrite',
      safetyNotes: draft.safetyNotes
    });

    this.isOpen.set(false);
    this.errorMessage.set('');
    this.conversationId.set(this.createConversationId());
    this.recentContext = undefined;
    void this.router.navigate(['/ask'], { queryParams: { source: 'ai-rewrite' } });
  }

  refineDraft(messageId: string): void {
    const message = this.messages().find(entry => entry.id === messageId);
    if (!message) return;
    this.sendChip('Refine this draft to be shorter, safer, and more anonymous.');
  }

  joinCommunity(messageId: string): void {
    const message = this.messages().find(entry => entry.id === messageId);
    const community = message?.structured?.kind === 'community' ? message.structured.community : null;
    if (!community) return;

    const parsedId = Number(String(community.communityId).trim());
    if (!Number.isFinite(parsedId)) return;

    const existing = this.categoriesStore.getById(parsedId);
    const navigateToCommunity = () => {
      this.categoriesStore.refresh();
      this.isOpen.set(false);
      this.errorMessage.set('');
      void this.router.navigate(['/category', parsedId]);
    };

    if (existing?.isFollowing) {
      navigateToCommunity();
      return;
    }

    this.categoryService.followCategory(parsedId).subscribe({
      next: () => navigateToCommunity(),
      error: () => {
        this.errorMessage.set('Could not join this community right now. Please try again.');
      }
    });
  }

  showMoreResults(messageId: string): void {
    const message = this.messages().find(entry => entry.id === messageId);
    const prompt = message?.followUpAction
      ? this.getFollowUpPrompt(message.followUpAction)
      : 'Show more results';
    this.sendChip(prompt);
  }

  navigateAction(action: ChatAction): void {
    if (!action.route?.length) return;

    this.isOpen.set(false);
    this.loadingLabel.set('Thinking...');
    this.errorMessage.set('');
    void this.router.navigate(action.route as any[], action.queryParams ? { queryParams: action.queryParams } : undefined);
  }

  clearGuidedMode(): void {
    if (!this.guidedMode()) return;
    this.guidedMode.set(null);
    this.errorMessage.set('');
    this.focusRequest.update(value => value + 1);
    this.touchConversation();
  }

  retryLastMessage(): void {
    if (this.isStreaming() || !this.assistantAvailable()) return;

    const text = this.inputText().trim() || this.getLatestUserMessageText();
    if (!text) return;

    this.errorMessage.set('');
    this.sendMessage(text);
  }

  startNewChat(): void {
    this.stopStreaming();
    this.resetConversationState();
    this.isOpen.set(true);
    this.hasUnread.set(false);
    this.focusRequest.update(value => value + 1);
    this.touchConversation();
  }

  private startAvailabilityWatch(): void {
    if (this.availabilitySubscription && !this.availabilitySubscription.closed) return;

    this.availabilitySubscription = this.healthNavService.watchAvailability().subscribe({
      next: availability => this.applyAvailability(availability),
      error: () => this.applyAvailability(null)
    });
  }

  private stopAvailabilityWatch(): void {
    this.availabilitySubscription?.unsubscribe();
    this.availabilitySubscription = undefined;
  }

  private applyAvailability(availability: AgentAvailabilityResponse | null): void {
    if (!availability) {
      this.widgetAvailable.set(false);
      return;
    }

    this.widgetVisible.set(availability.visible !== false);
    this.widgetAvailable.set(availability.available !== false);

    if (!availability.visible) {
      this.closePanel(true);
    }
  }

  private handleStreamEvent(messageId: string, event: AgentChatStreamEvent): void {
    const current = this.messages().find(entry => entry.id === messageId);
    if (!current) return;

    if (event.type === 'metadata') {
      this.lastAssistantTraceId = event.traceId ?? this.lastAssistantTraceId;
      this.recentContext = event.responseRecentContext ?? this.recentContext;
      const crisis = Boolean(event.crisis);
      this.updateMessage(messageId, {
        traceId: event.traceId ?? current.traceId ?? undefined,
        intent: event.intent ?? current.intent,
        useCase: event.useCase ?? current.useCase,
        agentName: event.agentName ?? current.agentName,
        crisis,
        blocked: Boolean(event.blocked),
        followUp: Boolean(event.followUp),
        followUpAction: event.followUpAction ?? current.followUpAction,
        structured: crisis ? { kind: 'crisis' } : current.structured,
        responseComplete: false
      });
      this.touchConversation();
      return;
    }

    if (event.type === 'status') {
      this.errorMessage.set(this.normalizeStreamEventMessage(event.message));
      this.touchConversation();
      return;
    }

    if (event.type === 'response_delta') {
      const nextText = `${current.text}${event.delta}`;
      const nextStructured = this.buildStructuredContent(
        current.intent ?? null,
        current.useCase ?? null,
        nextText,
        current.crisis
      );

      this.errorMessage.set('');
      this.updateMessage(messageId, {
        text: nextText,
        structured: nextStructured,
        isStreaming: true
      });
      this.touchConversation();
      return;
    }

    if (event.type === 'error') {
      const errorText = this.normalizeStreamEventMessage(event.message);
      this.lastAssistantTraceId = event.traceId ?? this.lastAssistantTraceId;
      this.updateMessage(messageId, {
        traceId: event.traceId ?? current.traceId,
        text: errorText,
        isStreaming: false,
        responseComplete: true
      });
      this.errorMessage.set(errorText);
      this.stopStreaming();
      this.restoreComposerState();
      this.touchConversation();
      return;
    }

    if (event.type === 'done') {
      this.lastAssistantTraceId = event.traceId ?? this.lastAssistantTraceId;
      this.updateMessage(messageId, {
        traceId: event.traceId ?? current.traceId ?? undefined
      });
      this.finishAssistantMessage(messageId);
    }
  }

  private finishAssistantMessage(messageId: string): void {
    const message = this.messages().find(entry => entry.id === messageId);
    if (!message) return;

    const structured = this.buildStructuredContent(
      message.intent ?? null,
      message.useCase ?? null,
      message.text,
      message.crisis
    );
    const traceId = message.traceId ?? this.lastAssistantTraceId;

    this.updateMessage(messageId, {
      traceId: traceId ?? undefined,
      isStreaming: false,
      responseComplete: true,
      feedbackSent: message.feedbackSent,
      structured: structured ?? message.structured ?? null
    });

    this.isStreaming.set(false);
    this.loadingLabel.set('Thinking...');
    this.pendingRequestContext = null;
    this.errorMessage.set('');
    if (!this.isOpen()) {
      this.hasUnread.set(true);
    }
    this.touchConversation();
  }

  private stopStreaming(): void {
    this.streamSubscription?.unsubscribe();
    this.streamSubscription = undefined;
    this.isStreaming.set(false);
    this.loadingLabel.set('Thinking...');
  }

  private touchConversation(): void {
    this.clearInactivityTimer();

    if (!this.isOpen() || this.isStreaming()) return;

    this.inactivityTimer = setTimeout(() => {
      this.conversationId.set(this.createConversationId());
      this.messages.set([this.createWelcomeMessage()]);
      this.inputText.set('');
      this.errorMessage.set('');
      this.hasUnread.set(false);
      this.lastAssistantTraceId = null;
      this.recentContext = undefined;
      this.pendingRequestContext = null;
      this.guidedMode.set(null);
      this.loadingLabel.set('Thinking...');
    }, 30 * 60 * 1000);
  }

  private clearInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = undefined;
    }
  }

  private resetConversationState(): void {
    this.conversationId.set(this.createConversationId());
    this.messages.set([this.createWelcomeMessage()]);
    this.inputText.set('');
    this.errorMessage.set('');
    this.isStreaming.set(false);
    this.lastAssistantTraceId = null;
    this.recentContext = undefined;
    this.pendingRequestContext = null;
    this.guidedMode.set(null);
    this.loadingLabel.set('Thinking...');
  }

  private createWelcomeMessage(): ChatMessage {
    return {
      id: this.createMessageId(),
      role: 'assistant',
      text: 'I can help you find peer posts, choose a community, or rewrite a question before you post.',
      isStreaming: false,
      crisis: false,
      blocked: false,
      followUp: false,
      responseComplete: true,
      timestamp: new Date(),
      isWelcomeMessage: true
    };
  }

  private buildSafetyGuardrailMessage(text: string): ChatMessage | null {
    if (!this.shouldShowSafetyGuardrail(text)) return null;

    return {
      id: this.createMessageId(),
      role: 'assistant',
      text: 'PeerLiv cannot diagnose or handle emergencies. If symptoms feel severe, urgent, or unsafe, contact a medical professional or local emergency services now.',
      isStreaming: false,
      crisis: false,
      blocked: false,
      followUp: false,
      responseComplete: true,
      timestamp: new Date(),
      isGuidancePrompt: true
    };
  }

  private shouldShowSafetyGuardrail(text: string): boolean {
    const normalized = text.toLowerCase();
    return /\b(chest pain|trouble breathing|can't breathe|cannot breathe|suicidal|kill myself|self harm|fainting|severe bleeding|stroke|heart attack|overdose)\b/.test(normalized);
  }

  private buildSuggestionChips(): ChatSuggestion[] {
    return [
      {
        label: 'Find similar posts',
        prompt: this.quickActionHint('Search posts'),
        guidedMode: 'search_posts_guided'
      },
      {
        label: 'Rewrite my post',
        prompt: this.quickActionHint('Rewrite'),
        guidedMode: 'rewrite_post_guided'
      },
      {
        label: 'Find a community',
        prompt: this.quickActionHint('Find the right community'),
        guidedMode: 'find_community_guided'
      }
    ];
  }

  private quickActionHint(label: string): string {
    switch (label) {
      case 'Search posts':
        return 'Search posts about ';
      case 'Rewrite':
        return 'Rewrite this post draft:\n\n';
      case 'Find the right community':
        return 'Find the right community for: ';
      default:
        return label;
    }
  }

  private getFollowUpPrompt(followUpAction: string): string {
    switch (followUpAction) {
      case 'summarize_search_results':
        return 'Summarise these results';
      case 'explain_search_results':
        return 'Explain this';
      case 'search_more':
        return 'Show more results';
      case 'search_related':
        return 'Search related topics';
      case 'search_refine_community':
        return 'Search this community only';
      case 'revise_rewrite':
        return 'Revise my draft';
      case 'simplify_response':
        return 'Simplify this';
      case 'explain_community':
        return 'Tell me more about this community';
      case 'alternate_community':
        return 'Show another community';
      case 'compare_communities':
        return 'Compare communities';
      case 'suggest_tags':
        return 'Suggest tags for my post';
      default:
        return 'Search more';
    }
  }

  private contextualActionsFor(message: ChatMessage | null): ChatAction[] {
    if (!message || message.isStreaming || !message.responseComplete) return [];
    const structured = message.structured;

    if (message.intent === 'rewrite_post') {
      const actions: ChatAction[] = [];

      if (structured?.kind === 'rewrite_post') {
        actions.push({ kind: 'use_draft', label: 'Use this draft' });
      }

      actions.push(
        { kind: 'prompt', label: 'Make it shorter', prompt: 'Make this draft shorter.' },
        { kind: 'prompt', label: 'Make it more anonymous', prompt: 'Make this draft more anonymous.' }
      );

      return actions;
    }

    if (message.intent === 'find_community' || (message.intent === 'search_posts' && message.useCase === 'newly_diagnosed')) {
      const resolvedCommunity = structured?.kind === 'community'
        ? structured.community
        : this.resolveCommunityFromMessage(message.text);
      const communityId = structured?.kind === 'community'
        ? structured.community.communityId
        : resolvedCommunity
          ? ('communityId' in resolvedCommunity
              ? resolvedCommunity.communityId
              : String(resolvedCommunity.id))
          : null;
      const actions: ChatAction[] = [];

      if (communityId) {
        actions.push(
          {
            kind: 'navigate',
            label: 'Post in this community',
            route: ['/ask'],
            queryParams: { categoryId: communityId, lockCategory: '1' }
          },
          {
            kind: 'navigate',
            label: 'Explore this community',
            route: ['/category', communityId]
          }
        );
      }

      actions.push({
        kind: 'prompt',
        label: 'Any other suitable community?',
        prompt: 'Any other suitable community?'
      });

      return actions;
    }

    if (message.intent === 'search_posts') {
      return [
        { kind: 'prompt', label: 'Summarize top 3', prompt: 'Summarize the top 3 results.' },
        { kind: 'prompt', label: 'Show more', prompt: 'Show me more peer discussions like these.' }
      ];
    }

    return [];
  }

  private getLatestAssistantMessage(): ChatMessage | null {
    const messages = this.messages();
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index].role === 'assistant' && messages[index].responseComplete && !messages[index].isGuidancePrompt) {
        return messages[index];
      }
    }
    return null;
  }

  private updateMessage(messageId: string, patch: Partial<ChatMessage>): void {
    this.messages.update(messages =>
      messages.map(message => (message.id === messageId ? { ...message, ...patch } : message))
    );
  }

  private buildStructuredContent(
    intent: string | null,
    useCase: string | null,
    text: string,
    crisis: boolean
  ): ChatStructuredContent | null {
    if (crisis) {
      return { kind: 'crisis' };
    }

    if (intent === 'rewrite_post') {
      const draft = this.extractRewriteDraft(text);
      return draft ? { kind: 'rewrite_post', draft } : null;
    }

    if (intent === 'peer_finder') {
      const peers = this.extractPeerCards(text);
      return peers.length ? { kind: 'peer_finder', peers } : null;
    }

    if (intent === 'find_community' || (intent === 'search_posts' && useCase === 'newly_diagnosed')) {
      const community = this.extractCommunityCard(text);
      return community ? { kind: 'community', community: community.community, starterPosts: community.starterPosts } : null;
    }

    if (intent === 'search_posts') {
      const posts = this.extractPostResults(text);
      return posts.length ? { kind: 'search_posts', posts } : null;
    }

    return null;
  }

  private resolveCommunityFromMessage(text: string): CommunityLookup | null {
    const match = text.match(
      /\b(?:start is|post this is|place to start is|place to post this is|in the)\s+(.+?)\s+community\b/i
    );
    const candidate = match?.[1]?.trim();

    return candidate ? this.findCommunityByName(candidate) : null;
  }

  private enterGuidedMode(mode: GuidedEntryMode): void {
    this.guidedMode.set(mode);

    const promptText = this.guidedPromptText(mode);
    this.messages.update(existing => [
      ...existing.filter(message => !message.isGuidancePrompt),
      {
        id: this.createMessageId(),
        role: 'assistant',
        text: promptText,
        isStreaming: false,
        crisis: false,
        blocked: false,
        followUp: false,
        responseComplete: true,
        timestamp: new Date(),
        isGuidancePrompt: true
      }
    ]);

    this.isOpen.set(true);
    this.hasUnread.set(false);
    this.errorMessage.set('');
    this.inputText.set('');
    this.focusRequest.update(value => value + 1);
    this.touchConversation();
  }

  private resolveGuidedMode(prompt: string): GuidedEntryMode | null {
    const normalized = prompt.trim().toLowerCase();

    if (normalized === 'search posts' || normalized === 'search peer posts' || normalized === 'search peer posts about this topic') {
      return 'search_posts_guided';
    }

    if (normalized === 'rewrite' || normalized === 'help me write a post' || normalized === 'rewrite this post') {
      return 'rewrite_post_guided';
    }

    if (normalized === 'find community' || normalized === 'find the right community' || normalized === 'find my community' || normalized === 'community recommendation') {
      return 'find_community_guided';
    }

    return null;
  }

  private getLatestUserMessageText(): string {
    const messages = this.messages();
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.role === 'user') {
        return message.text.trim();
      }
    }

    return '';
  }

  private buildGuidedMessageText(mode: GuidedEntryMode, text: string): string {
    switch (mode) {
      case 'search_posts_guided':
        return `Search posts about ${text}.`;
      case 'rewrite_post_guided':
        return `Rewrite this post draft:\n\n${text}`;
      case 'find_community_guided':
        return `Find the right community for: ${text}`;
    }
  }

  private modeForGuidedMode(mode: GuidedEntryMode | null): AgentChatRequest['mode'] {
    switch (mode) {
      case 'search_posts_guided':
        return 'search_posts';
      case 'rewrite_post_guided':
        return 'rewrite_post';
      case 'find_community_guided':
        return 'find_community';
      default:
        return null;
    }
  }

  private buildInputPlaceholder(mode: GuidedEntryMode | null): string {
    if (!mode) {
      return 'Ask PeerLiv about posts, communities, or a draft...';
    }

    switch (mode) {
      case 'search_posts_guided':
        return 'Search for a symptom, experience, or topic...';
      case 'rewrite_post_guided':
        return 'Paste the draft you want me to improve...';
      case 'find_community_guided':
        return 'Describe your condition, symptoms, or situation...';
    }

    return 'Ask PeerLiv about posts, communities, or a draft...';
  }

  private buildInputHelperText(mode: GuidedEntryMode | null): string {
    if (!mode) {
      return '';
    }

    switch (mode) {
      case 'search_posts_guided':
        return 'Examples: blood sugar and diet, PCOS hair fall, thyroid fatigue';
      case 'rewrite_post_guided':
        return 'I can make it clearer, shorter, warmer, or more anonymous.';
      case 'find_community_guided':
        return 'Examples: diagnosed with PCOS, thyroid fatigue, anxiety and sleep';
    }

    return '';
  }

  private buildLoadingLabel(mode: GuidedEntryMode | null, outgoingText: string): string {
    if (mode === 'search_posts_guided') {
      return 'Searching PeerLiv posts...';
    }

    if (mode === 'rewrite_post_guided') {
      return 'Drafting a safer version...';
    }

    if (mode === 'find_community_guided') {
      return 'Finding relevant communities...';
    }

    const normalized = outgoingText.trim().toLowerCase();

    if (
      normalized.startsWith('search posts about') ||
      normalized.startsWith('summarize top 3') ||
      normalized.startsWith('summarise top 3') ||
      normalized.startsWith('show more') ||
      normalized.includes('search this community')
    ) {
      return 'Searching PeerLiv posts...';
    }

    if (
      normalized.startsWith('rewrite this post draft:') ||
      normalized.includes('shorter') ||
      normalized.includes('more anonymous') ||
      normalized.includes('refine this draft')
    ) {
      return 'Drafting a safer version...';
    }

    if (
      normalized.startsWith('find the right community for:') ||
      normalized.startsWith('any other suitable community')
    ) {
      return 'Finding relevant communities...';
    }

    return 'Preparing a helpful response...';
  }

  private buildAssistantUnavailableMessage(): string {
    return 'Health Navigator is taking a short break right now. You can keep your conversation open and try again in a moment.';
  }

  private normalizeChatError(error: unknown): string {
    if (isAiRateLimitError(error)) {
      return 'You have reached the current AI limit. Please try again in a little while.';
    }

    if (error instanceof Error) {
      return this.normalizeStreamEventMessage(error.message);
    }

    return 'Health Navigator could not respond just now. Your message is still here, so you can try again.';
  }

  private normalizeStreamEventMessage(message: string | null | undefined): string {
    const normalized = (message ?? '').trim();
    if (!normalized) {
      return 'Health Navigator could not respond just now. Your message is still here, so you can try again.';
    }

    const lower = normalized.toLowerCase();

    if (
      lower.includes('429') ||
      lower.includes('rate limit') ||
      lower.includes('usage limit')
    ) {
      return 'You have reached the current AI limit. Please try again in a little while.';
    }

    if (
      lower.includes('unavailable') ||
      lower.includes('failed with status 503') ||
      lower.includes('failed with status 502') ||
      lower.includes('failed with status 500') ||
      lower.includes('network') ||
      lower.includes('fetch')
    ) {
      return 'Health Navigator could not respond just now. Your message is still here, so you can try again.';
    }

    return normalized;
  }

  private restoreComposerState(): void {
    const pending = this.pendingRequestContext;
    this.pendingRequestContext = null;
    if (!pending) return;

    this.inputText.set(pending.text);
    this.guidedMode.set(pending.guidedMode);
    this.isOpen.set(true);
    this.focusRequest.update(value => value + 1);
  }

  private isRateLimitMessage(message: string): boolean {
    const lower = message.trim().toLowerCase();
    return lower.includes('current ai limit') || lower.includes('rate limit') || lower.includes('usage limit');
  }

  private guidedPromptText(mode: GuidedEntryMode): string {
    switch (mode) {
      case 'search_posts_guided':
        return 'What would you like to search for in the community? Share a symptom, experience, or topic and I will find discussions about it.';
      case 'rewrite_post_guided':
        return 'Paste the draft you want me to improve. I can help make it shorter, warmer, or more anonymous.';
      case 'find_community_guided':
        return 'What are you dealing with right now? Tell me the condition, symptoms, or situation, and I will suggest the best community.';
    }
  }

  private extractPostResults(text: string): PostResultVm[] {
    const lines = (text ?? '').split('\n').map(line => line.trim()).filter(Boolean);
    const results: PostResultVm[] = [];

    for (const line of lines) {
      const match = line.match(/^[-*]\s*(?<title>[^:\n]+):\s*(?<excerpt>.*?)(?:\s+\((?<community>[^()]+)\))?(?:\s+\[[^\]]+\])?$/);
      if (!match?.groups) continue;

      const title = match.groups['title']?.trim();
      const excerpt = match.groups['excerpt']?.trim();
      const community = match.groups['community']?.trim() ?? '';
      if (!title || !excerpt) continue;

      const resolvedCommunity = this.findCommunityByName(community);
      const fallbackCommunityId = this.normalizeCommunityName(community).replace(/\s+/g, '-');
      const communityId =
        String(resolvedCommunity?.slug ?? '').trim().toLowerCase() ||
        this.resolveCommunityId(community) ||
        fallbackCommunityId ||
        '';
      results.push({
        postId: this.toSafeId(`${title}-${results.length + 1}`),
        title,
        excerpt,
        communityId,
        communityName: resolvedCommunity?.name?.trim() || community,
        replyCount: Math.max(0, 8 - results.length),
        helpfulReplyCount: Math.max(0, 3 - results.length)
      });
    }

    return results.slice(0, 5);
  }

  private extractCommunityCard(text: string): { community: CommunityJoinVm; starterPosts: string[] } | null {
    const matched = this.resolveCommunityName(text);
    const community = matched
      ? this.findCommunityByName(matched)
      : this.categoriesStore.getById(this.resolveCommunityIdFromRoute());
    if (!community) return null;

    const lookup = community as CommunityLookup;
    const communityId = String(lookup.id);
    const description = lookup.tagline ?? lookup.description ?? `A place to talk about ${lookup.name.toLowerCase()}.`;
    return {
      community: {
        communityId,
        communityName: lookup.name,
        memberCount: Number(lookup.members ?? lookup.memberCount ?? 0),
        description
      },
      starterPosts: this.extractStarterPosts(text, lookup.name)
    };
  }

  private extractStarterPosts(text: string, communityName: string): string[] {
    const lines = (text ?? '').split('\n').map(line => line.trim()).filter(Boolean);
    const starters = lines
      .filter(line => !/^Title:/i.test(line) && !/^Body:/i.test(line) && !/^Safety notes:/i.test(line))
      .filter(line => !line.toLowerCase().includes('community'))
      .slice(0, 2);

    if (starters.length) return starters;

    return [
      `What should I ask first in ${communityName}?`,
      `What helped you feel more in control after a new diagnosis?`
    ];
  }

  private extractPeerCards(text: string): Array<{ handle: string; initials: string; sharedExperience: string }> {
    const lines = (text ?? '').split('\n').map(line => line.trim()).filter(Boolean);
    const peers: Array<{ handle: string; initials: string; sharedExperience: string }> = [];

    for (const line of lines) {
      const cleaned = line.replace(/^[-*]\s*/, '');
      const match = cleaned.match(/^(?:@)?(?<handle>[A-Za-z0-9_.-]{2,32})\s*:\s*(?<experience>.+)$/);
      const handle = match?.groups?.['handle']?.trim() ?? `peer${peers.length + 1}`;
      const experience = match?.groups?.['experience']?.trim() ?? cleaned;
      peers.push({
        handle,
        initials: this.getInitials(handle),
        sharedExperience: experience
      });
      if (peers.length >= 3) break;
    }

    return peers;
  }

  private extractRewriteDraft(text: string): RewriteDraftVm | null {
    const titleMatch = text.match(/(?:^|\n)\s*Title:\s*(.+?)(?=\n|$)/i);
    const bodyMatch = text.match(/(?:^|\n)\s*Body:\s*([\s\S]*?)(?=\n\s*Safety notes:\s*|\s*$)/i);
    const title = titleMatch?.[1]?.trim() ?? '';
    const body = bodyMatch?.[1]?.trim() ?? '';

    if (!title || !body) return null;

    const notesMatch = text.match(/(?:^|\n)\s*Safety notes:\s*([\s\S]*)$/i);
    const safetyNotes = notesMatch?.[1]
      ?.split('\n')
      .map(line => line.replace(/^\s*[-*]\s*/, '').trim())
      .filter(Boolean) ?? [];

    return { title, body, safetyNotes };
  }

  private findCommunityByName(name: string): CommunityLookup | null {
    const normalized = this.normalizeCommunityName(name);
    return this.categoriesStore.allCategoryCards().find(category => {
      const normalizedName = this.normalizeCommunityName(category.name);
      const normalizedSlug = this.normalizeCommunityName(category.slug ?? '');
      return normalizedName === normalized || normalizedSlug === normalized || normalizedName.includes(normalized) || normalized.includes(normalizedName);
    }) as CommunityLookup | null;
  }

  private resolveCommunityName(text: string): string | null {
    const match = text.match(/(?:best place to start is the|start is the|considering is the|the)\s+(.+?)\s+community/i);
    return match?.[1]?.trim() ?? null;
  }

  private resolveCommunityId(value?: string | null): string | null {
    if (!value) return null;
    const normalized = this.normalizeCommunityName(value).replace(/\s+/g, '-');
    const community = this.categoriesStore.allCategoryCards().find(category => {
      const slug = this.normalizeCommunityName(category.slug ?? '').replace(/\s+/g, '-');
      const name = this.normalizeCommunityName(category.name).replace(/\s+/g, '-');
      return slug === normalized || name === normalized || name.includes(normalized) || normalized.includes(name);
    });

    return community ? String(community.slug ?? community.id).trim().toLowerCase() : null;
  }

  private resolveCommunityIdFromRoute(): string | undefined {
    const match = this.router.url.match(/\/(?:category|community|communities)\/([^/?#]+)/i);
    return match?.[1] ? decodeURIComponent(match[1]) : undefined;
  }

  private normalizeCommunityName(value: string): string {
    return value
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\b(community|support|condition|conditions)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private getInitials(value: string): string {
    return getAvatarInitials(value);
  }

  private toSafeId(value: string): string {
    const safe = value.trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
    return safe || 'post';
  }

  private createMessageId(): string {
    return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  private createConversationId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
